/**
 * Counts how many requests are currently in flight on the shared API client.
 *
 * Purpose: let the app answer one question — "has the network gone quiet?".
 * The boot loading screen (components/common/AppBootOverlay.tsx) uses that to
 * stay up until the first screen has finished fetching everything it needs,
 * instead of letting the UI assemble piece by piece in front of the user.
 *
 * Only the shared axios instance in services/api.ts reports here. The dedicated
 * long-running clients (Conductor / Smart Flow generation) deliberately do NOT:
 * an AI generation can run for minutes and must never hold a loading screen up.
 */

type ActivityListener = (inFlight: number) => void;

let inFlight = 0;
const listeners = new Set<ActivityListener>();

const notify = () => {
  listeners.forEach((listener) => {
    try {
      listener(inFlight);
    } catch {
      // A broken listener must never break request handling.
    }
  });
};

/** Call when a request is dispatched. */
export const beginApiRequest = (): void => {
  inFlight += 1;
  notify();
};

/** Call when a request finally settles (after any retry/refresh handling). */
export const endApiRequest = (): void => {
  // Never go negative: one missed increment must not make the counter unusable.
  inFlight = Math.max(0, inFlight - 1);
  notify();
};

/** How many shared-client requests are outstanding right now. */
export const getInFlightRequests = (): number => inFlight;

/** Subscribe to changes; returns an unsubscribe function. */
export const subscribeApiActivity = (listener: ActivityListener): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};
